import { useState } from 'react';
import {
  DEFAULT_PROMO_DESIGN,
  DEFAULT_PROMO_TITLE,
  interpolatePromoCopy,
  promoFontWeightCss,
  uniquePromoGifts,
  type PromoDesign,
  type PromoPreviewGift,
} from './promoDefaults';

export interface PromoPreviewProps {
  design?: Partial<PromoDesign>;
  title?: string;
  subtitle?: string;
  triggerValue?: string;
  gifts?: PromoPreviewGift[];
  mode?: 'desktop' | 'mobile';
  /** Compact strip for Widgets list */
  variant?: 'full' | 'card';
}

const PLACEHOLDER =
  'data:image/svg+xml,' +
  encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" width="80" height="80"><rect fill="#EEE8DC" width="80" height="80"/><text x="50%" y="54%" text-anchor="middle" fill="#B8B0A4" font-size="11" font-family="sans-serif">Gift</text></svg>',
  );

/** Live / campaign preview of the PDP promotion widget (style only). */
export function PromoPreview({
  design,
  title,
  subtitle,
  triggerValue = '100.00',
  gifts,
  mode = 'desktop',
  variant = 'full',
}: PromoPreviewProps) {
  const d: PromoDesign = { ...DEFAULT_PROMO_DESIGN, ...design };
  const isCard = variant === 'card';
  const isMobile = mode === 'mobile';
  const heading = title || DEFAULT_PROMO_TITLE;
  const sub = interpolatePromoCopy(
    subtitle || 'Spend {{trigger_value}} to receive gifts',
    triggerValue,
  );
  const items = uniquePromoGifts(
    gifts && gifts.length
      ? gifts
      : [
          {
            id: 'demo-1',
            title: 'Reward product',
            priceLabel: '$37.31',
            offeredLabel: 'Free',
          },
        ],
  );
  const [idx, setIdx] = useState(0);
  const canSlide = items.length > 1;
  const go = (dir: number) => {
    if (!canSlide) return;
    setIdx((i) => (i + dir + items.length) % items.length);
  };

  return (
    <div
      style={{
        fontFamily: d.fontFamily,
        background: isCard ? '#F0F2F4' : '#F4F5F7',
        padding: isCard ? 16 : isMobile ? 12 : 20,
        borderRadius: 12,
        pointerEvents: isCard ? 'none' : undefined,
      }}
    >
      <div
        style={{
          background: d.backgroundColor,
          color: d.textColor,
          border: `1px solid ${d.borderColor}`,
          borderRadius: d.borderRadius,
          padding: isCard ? '14px 14px 12px' : '18px 16px 14px',
          maxWidth: isMobile ? 360 : 440,
          margin: '0 auto',
          boxShadow: '0 1px 0 rgba(0,0,0,.03)',
        }}
      >
        <div
          style={{
            fontWeight: promoFontWeightCss(d.titleFontWeight),
            fontSize: isCard ? 13 : 15,
            letterSpacing: '0.02em',
            lineHeight: 1.25,
            marginBottom: 4,
          }}
        >
          {heading}
        </div>
        <div
          style={{
            fontWeight: promoFontWeightCss(d.subtitleFontWeight),
            fontSize: isCard ? 12 : 13,
            color: d.mutedColor,
            marginBottom: 12,
            lineHeight: 1.35,
          }}
        >
          {sub}
        </div>

        <div style={{ overflow: 'hidden' }}>
          <div
            style={{
              display: 'flex',
              gap: 10,
              transform: `translateX(-${idx * (isCard ? 78 : 72)}%)`,
              transition: 'transform 0.25s ease',
            }}
          >
            {items.map((g) => (
              <div
                key={g.id}
                style={{
                  flex: `0 0 ${items.length === 1 ? '100%' : isCard ? '78%' : '72%'}`,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  background: d.giftCardBackground,
                  border: `1px solid ${d.giftCardBorder}`,
                  borderRadius: Math.max(10, d.borderRadius - 4),
                  padding: 10,
                  minWidth: 0,
                }}
              >
                <img
                  src={g.imageUrl || PLACEHOLDER}
                  alt=""
                  width={64}
                  height={64}
                  style={{
                    width: isCard ? 52 : 64,
                    height: isCard ? 52 : 64,
                    objectFit: 'cover',
                    borderRadius: 8,
                    background: '#F3EEE4',
                    flexShrink: 0,
                  }}
                />
                <div style={{ minWidth: 0 }}>
                  <div
                    style={{
                      fontWeight: 600,
                      fontSize: isCard ? 12 : 14,
                      lineHeight: 1.3,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                    }}
                  >
                    {g.title}
                  </div>
                  <div
                    style={{
                      marginTop: 4,
                      fontSize: isCard ? 12 : 13,
                      display: 'flex',
                      alignItems: 'baseline',
                      gap: 6,
                      flexWrap: 'wrap',
                    }}
                  >
                    <span style={{ fontWeight: 700, color: d.accentColor }}>
                      {g.offeredLabel || g.priceLabel || 'Free'}
                    </span>
                    {g.priceLabel &&
                    g.offeredLabel &&
                    g.offeredLabel !== g.priceLabel ? (
                      <span
                        style={{
                          color: d.comparePriceColor,
                          textDecoration: 'line-through',
                        }}
                      >
                        {g.priceLabel}
                      </span>
                    ) : null}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {canSlide && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 10,
              marginTop: 12,
            }}
          >
            <button
              type="button"
              onClick={() => go(-1)}
              aria-label="Previous gift"
              style={{
                width: 28,
                height: 28,
                borderRadius: 8,
                border: `1px solid ${d.giftCardBorder}`,
                background: d.giftCardBackground,
                color: d.mutedColor,
                cursor: 'pointer',
                fontSize: 14,
                lineHeight: 1,
              }}
            >
              ‹
            </button>
            <div style={{ display: 'flex', gap: 6 }}>
              {items.map((g, i) => (
                <span
                  key={g.id}
                  style={{
                    width: i === idx ? 16 : 6,
                    height: 6,
                    borderRadius: 99,
                    background: i === idx ? d.accentColor : '#D5D0C6',
                  }}
                />
              ))}
            </div>
            <button
              type="button"
              onClick={() => go(1)}
              aria-label="Next gift"
              style={{
                width: 28,
                height: 28,
                borderRadius: 8,
                border: `1px solid ${d.giftCardBorder}`,
                background: d.giftCardBackground,
                color: d.mutedColor,
                cursor: 'pointer',
                fontSize: 14,
                lineHeight: 1,
              }}
            >
              ›
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
